// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { padding } from 'polished';
import get from 'lodash/get';
import glamorous, { Span } from 'glamorous';
import Tappable from 'app/components/commons/Tappable';
import GiftItem from 'app/components/giftstore/GiftItem';
import Modal from 'app/components/commons/Modal';
import GiftDetail from 'app/components/giftstore/GiftDetail';
import Title from 'app/components/commons/Title';
import * as giftstoreActions from 'app/actions/giftstoreActions';
import Spinner from 'app/components/commons/Spinner';
import t from 'app/utils/translate';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import CashOutSoon from './CashOutSoon';
import GridItem from './GridItem';

type Props = {
  balance?: number,
  errorMessages?: Array<Object>,
  fetchProducts: () => void,
  giftStoreName: string,
  gifts: Array<Object>,
  history: Object,
  isLoading: boolean,
  isPurchasing: boolean,
  isSignedIn: boolean,
  isVerified: boolean,
  promoText: string,
  redeemProduct: () => void,
  resetErrors: () => void,
  resetState: () => void,
  siteName: string
};

type State = {
  selected: ?string,
  showIsVerifiedModal: boolean
};

const GridContainer = glamorous.div(
  {
    display: 'grid',
    // gridAutoRows: '1fr',
    gridGap: 12,
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  ({ theme }) => ({
    [theme.mediaQuery.tabletPortraitAndUp]: {
      gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))'
    }
  })
);

const SpinnerWrapper = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  padding: 20
});

const Warning = glamorous.div(({ theme }) => ({
  color: theme.color.basis1,
  fontSize: theme.fontSize.default,
  padding: theme.spacing.small,
  textAlign: 'center',
  textTransform: 'uppercase'
}));

const HyperLink = glamorous(Tappable)({
  color: '#5bc0de',
  paddingLeft: 5
});

const GiftStoreDisclaimer = glamorous.div(({ theme }) => ({
  fontSize: '0.75rem',
  ...padding('1rem', 6),
  [theme.mediaQuery.desktopAndUp]: {
    ...padding(null, 20)
  }
}));

class GiftStore extends React.Component<Props, State> {
  props: Props;

  state: State = {
    selected: null,
    showIsVerifiedModal: false
  };

  static defaultProps = {
    balance: undefined,
    errorMessages: undefined
  };

  componentDidMount() {
    const { fetchProducts } = this.props;

    fetchProducts();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isPurchasing } = this.props;

    if (isPurchasing && !nextProps.isPurchasing && !nextProps.errorMessages) {
      // just purchased an item
      this.setState({
        selected: null
      });
    }
  }

  componentWillUnmount() {
    const { resetState } = this.props;

    resetState();
  }

  handleItemClick = id => {
    const { isSignedIn, isVerified } = this.props;

    if (!isSignedIn) {
      this.openGlobalSignInModal();
    } else if (isVerified) {
      this.setState({
        selected: id
      });
    } else {
      this.setState({
        showIsVerifiedModal: true
      });
    }
  };

  openGlobalSignInModal = () => {
    // const { history } = this.props;
    // history.push({
    //   search: '?modal=signin',
    // });
    if (window.portal) window.portal.client.showLogin();
  };

  render() {
    const {
      balance,
      errorMessages,
      giftStoreName,
      gifts,
      redeemProduct,
      isPurchasing,
      isLoading,
      isSignedIn,
      resetErrors,
      siteName
    } = this.props;
    const { selected, showIsVerifiedModal } = this.state;
    const selectedGift = gifts.find(g => g.id === selected);

    return (
      <div>
        <Helmet title={t(giftStoreName)} />
        <NotificationHandler
          afterCond={!isPurchasing && !errorMessages}
          beforeCond={isPurchasing}
          type="success"
        >
          {t('Gift successfully purchased')}
        </NotificationHandler>
        <Title>{t(giftStoreName)}</Title>
        <hr />
        {isLoading ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : (
          <div>
            <GridContainer>
              <CashOutSoon balance={balance} />
              {gifts.map(gift => (
                <GridItem disabled>
                  <GiftItem key={gift.name} disabled onClick={this.handleItemClick} {...gift} />
                </GridItem>
              ))}
            </GridContainer>
            <GiftStoreDisclaimer>
              {t(`The merchants represented are not sponsors of the rewards or otherwise
                  affiliated with *site_name*. The logos and other identifying marks attached are
                  trademarks of and owned by each represented company and/or its affiliates. Please
                  visit each company's website for additional terms and conditions.`).replace(
                '*site_name*',
                siteName
              )}
            </GiftStoreDisclaimer>
            {selectedGift && isSignedIn && (
              <Modal
                isOpen
                onCancel={() => {
                  resetErrors();
                  this.setState({ selected: null });
                }}
                kind="medium"
              >
                <GiftDetail
                  errorMessages={errorMessages}
                  id={selectedGift.id}
                  isPurchasing={isPurchasing}
                  image={selectedGift.image}
                  name={selectedGift.name}
                  onBuy={redeemProduct}
                  onCancel={resetErrors}
                  price={selectedGift.price}
                />
              </Modal>
            )}
            {showIsVerifiedModal && isSignedIn && (
              <Modal
                isOpen
                onCancel={() => {
                  resetErrors();
                  this.setState({ showIsVerifiedModal: false });
                }}
                kind="medium"
              >
                <Warning>
                  <Span fontSize="1.2em" paddingRight={4} className="ion-android-warning" />
                  {t('Your account must be fully verified to purchase a gift.')}
                  <HyperLink component={Link} to="/account/profile">
                    {t('Verify now')}
                    <Span paddingLeft={4} className="ion-android-open" />
                  </HyperLink>
                </Warning>
              </Modal>
            )}
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    gifts: state.giftstore.products,
    promoText: state.giftstore.promoText,
    balance: get(state.user, 'currentUser.balance'),
    errorMessages: get(state.giftstore, 'errors.messages'),
    giftStoreName: state.settings.giftStoreName,
    isLoading: state.giftstore.isLoading,
    isPurchasing: state.giftstore.isPurchasing,
    isSignedIn: state.user.isSignedIn,
    isVerified:
      get(state.user, 'currentUser.id_verified') &&
      get(state.user, 'currentUser.phone_verified') &&
      get(state.user, 'currentUser.email_verified'),
    siteName: state.settings.siteName
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return {
    fetchProducts() {
      dispatch(giftstoreActions.fetchProducts());
    },
    redeemProduct(...args) {
      dispatch(giftstoreActions.redeemProduct(...args));
    },
    resetErrors() {
      dispatch(giftstoreActions.resetErrors());
    },
    resetState() {
      dispatch(giftstoreActions.resetState());
    }
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(GiftStore);
